import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { useCustomerAuthStore } from './customerAuthStore.js';

let instance: AxiosInstance | undefined;
let refreshing: Promise<string> | null = null;

const apiBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const portalBase = `${apiBase}/portal`;

/**
 * Axios client for /api/v1/portal/*. Separate from the tenant + platform
 * clients so the three auth pipelines (and their path-isolated refresh
 * cookies) never cross.
 */
export function customerApi(): AxiosInstance {
  if (instance) return instance;
  instance = axios.create({
    baseURL: portalBase,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = useCustomerAuthStore.getState().accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (err) => {
      const original = err.config as
        | (InternalAxiosRequestConfig & { _retried?: boolean })
        | undefined;
      const status = err.response?.status as number | undefined;
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        try {
          const newAccess = await getRefresh();
          if (newAccess) {
            original.headers = original.headers ?? {};
            original.headers.Authorization = `Bearer ${newAccess}`;
            return instance!.request(original);
          }
        } catch {
          // fall through to logout
        }
        useCustomerAuthStore.getState().clear();
      }
      throw err;
    },
  );

  return instance;
}

async function getRefresh(): Promise<string> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const r = await axios.post<{ accessToken: string; expiresIn: number }>(
        `${portalBase}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      useCustomerAuthStore.getState().setToken(r.data.accessToken, r.data.expiresIn);
      return r.data.accessToken;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}
